import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Summarize as SummarizeIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../services/api';
import therapistStyles from './styles/therapistStyles';

const SessionSummaryView = ({ clientId, conversationId, token }) => {
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [summaryData, setSummaryData] = useState({
    therapistSummary: null,
    clientSummary: null,
    reflectionPrompts: [],
    summariesLoaded: false
  });
  
  // Editing state
  const [isEditingClientSummary, setIsEditingClientSummary] = useState(false);
  const [editableClientSummaryText, setEditableClientSummaryText] = useState('');
  const [clientSummaryEditLoading, setClientSummaryEditLoading] = useState(false);

  // Load existing summaries on mount
  useEffect(() => {
    if (conversationId && token) {
      loadExistingSummaries();
    }
  }, [conversationId, token]);

  const loadExistingSummaries = async () => {
    if (!conversationId || !token) return;
    
    setLoading(true);
    setError('');
    try {
      console.log('Loading summaries for conversation:', conversationId);
      const data = await api.getTherapistSessionSummary(conversationId, token);
      
      if (data) {
        setSummaryData({
          therapistSummary: data.therapistSummary || null,
          clientSummary: data.clientSummary || null,
          reflectionPrompts: data.reflectionPrompts || [],
          summariesLoaded: true
        });
      }
    } catch (err) {
      if (!err.message?.toLowerCase().includes('not found')) {
        setError('Failed to load summaries: ' + err.message);
      } else {
        console.log('No summaries found yet');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!conversationId || !token || summaryLoading) return;
    
    setSummaryLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Generating summaries for conversation:', conversationId);
      const response = await api.generateSessionSummary(conversationId, token);
      
      setSummaryData({
        therapistSummary: response.therapistSummary || null,
        clientSummary: response.clientSummary || null,
        reflectionPrompts: response.reflectionPrompts || [],
        summariesLoaded: true
      });
      
      setSuccess('Summaries generated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err.message || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleStartEditClientSummary = () => {
    if (summaryData.clientSummary) {
      setEditableClientSummaryText(summaryData.clientSummary);
      setIsEditingClientSummary(true);
      setError('');
    }
  };

  const handleCancelEditClientSummary = () => {
    setIsEditingClientSummary(false);
    setEditableClientSummaryText('');
  };

  const handleSaveEditedClientSummary = async () => {
    if (!conversationId || !token || !editableClientSummaryText.trim()) {
      setError('Summary text cannot be empty.');
      return;
    }
    
    setClientSummaryEditLoading(true);
    setError('');
    
    try {
      const response = await api.updateClientSummary(conversationId, editableClientSummaryText, token);
      setSummaryData(prev => ({
        ...prev,
        clientSummary: response.clientSummary
      }));
      setIsEditingClientSummary(false);
      setSuccess('Client summary updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving edited client summary:', err);
      setError(err.message || 'Failed to save edited summary.');
    } finally {
      setClientSummaryEditLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!conversationId) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No conversation selected. Please select a conversation from the Conversations tab.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Session Summaries & Insights
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateSummary}
          disabled={summaryLoading}
          startIcon={summaryLoading ? <CircularProgress size={16} color="inherit" /> : <SummarizeIcon />}
        >
          {summaryLoading ? 'Processing...' : 
           (summaryData.summariesLoaded && (summaryData.therapistSummary || summaryData.clientSummary) 
            ? 'Regenerate Summaries' 
            : 'Generate Summaries')}
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Therapist Summary */}
      {summaryData.therapistSummary && !summaryLoading && (
        <Paper sx={{ ...therapistStyles.mainCard, p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FF8C00' }}>
            Therapist Summary
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {summaryData.therapistSummary}
          </Typography>
        </Paper>
      )}

      {/* Client Summary */}
      {summaryData.clientSummary && !summaryLoading && (
        <Paper sx={{ ...therapistStyles.mainCard, p: 3, mb: 3, bgcolor: 'blue.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#42A5F5' }}>
              Client Summary (What Client Sees)
            </Typography>
            {!isEditingClientSummary && (
              <Tooltip title="Edit Client Summary">
                <IconButton onClick={handleStartEditClientSummary} size="small" disabled={clientSummaryEditLoading} aria-label="Edit client summary">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {isEditingClientSummary ? (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={editableClientSummaryText}
                onChange={(e) => setEditableClientSummaryText(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                disabled={clientSummaryEditLoading}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button 
                  onClick={handleCancelEditClientSummary} 
                  disabled={clientSummaryEditLoading}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEditedClientSummary}
                  variant="contained"
                  disabled={clientSummaryEditLoading || !editableClientSummaryText.trim()}
                  startIcon={clientSummaryEditLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {summaryData.clientSummary}
            </Typography>
          )}
        </Paper>
      )}

      {/* Reflection Prompts */}
      {summaryData.reflectionPrompts && summaryData.reflectionPrompts.length > 0 && !summaryLoading && (
        <Paper sx={{ ...therapistStyles.mainCard, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Reflection Prompts for Client
          </Typography>
          <List>
            {summaryData.reflectionPrompts.map((prompt, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={`${index + 1}. ${prompt}`}
                    primaryTypographyProps={{ sx: { lineHeight: 1.6 } }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* No summaries message */}
      {!summaryData.summariesLoaded && !summaryLoading && (
        <Alert severity="info">
          No summaries have been generated yet. Click "Generate Summaries" to create session insights.
        </Alert>
      )}
    </Box>
  );
};

export default SessionSummaryView;